import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { useLanguage } from '../../contexts/LanguageContext';
import { doctorUpdateMe } from '../services/doctorApi';
import { useDoctorSession } from '../contexts/DoctorSessionContext';

export const DOCTOR_TOUR_STEPS = [
  {
    id: 'welcome',
    route: ROUTES.DOCTOR_PATIENTS,
    titleKey: 'doctorTourWelcomeTitle',
    bodyKey: 'doctorTourWelcomeBody',
  },
  {
    id: 'add-patient',
    target: 'doctor-add-patient',
    route: ROUTES.DOCTOR_PATIENTS,
    titleKey: 'doctorTourAddTitle',
    bodyKey: 'doctorTourAddBody',
    placement: 'left',
  },
  {
    id: 'record',
    target: 'doctor-record-mic',
    route: ROUTES.DOCTOR_PATIENTS,
    titleKey: 'doctorTourRecordTitle',
    bodyKey: 'doctorTourRecordBody',
    placement: 'top',
  },
  {
    id: 'notes',
    target: 'doctor-nav-notes',
    route: ROUTES.DOCTOR_NOTES,
    titleKey: 'doctorTourNotesTitle',
    bodyKey: 'doctorTourNotesBody',
    placement: 'top',
  },
  {
    id: 'settings',
    target: 'doctor-nav-settings',
    route: ROUTES.DOCTOR_PATIENTS,
    titleKey: 'doctorTourSettingsTitle',
    bodyKey: 'doctorTourSettingsBody',
    placement: 'left',
  },
];

const PAD = 10;

function measureTarget(targetId) {
  if (!targetId) return null;
  const el = document.querySelector(`[data-tour="${targetId}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 && r.height < 2) return null;
  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
    bottom: r.bottom,
    right: r.right,
  };
}

function tooltipStyle(rect, placement) {
  if (!rect) {
    return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'min(22rem, calc(100vw - 2rem))',
    };
  }

  const gap = 14;
  const maxW = Math.min(320, window.innerWidth - 24);
  const centeredLeft = Math.min(
    Math.max(12, rect.left + rect.width / 2 - maxW / 2),
    window.innerWidth - maxW - 12
  );

  if (placement === 'left') {
    const left = Math.max(12, rect.left - maxW - gap);
    const top = Math.min(
      Math.max(12, rect.top + rect.height / 2 - 60),
      window.innerHeight - 180
    );
    return { top, left, width: maxW };
  }

  if (placement === 'right') {
    const left = Math.min(rect.right + gap, window.innerWidth - maxW - 12);
    const top = Math.min(
      Math.max(12, rect.top + rect.height / 2 - 60),
      window.innerHeight - 180
    );
    return { top, left, width: maxW };
  }

  // top or default bottom
  const spaceBelow = window.innerHeight - rect.bottom;
  const preferTop = placement === 'top' || spaceBelow < 160;
  if (preferTop) {
    return {
      bottom: window.innerHeight - rect.top + gap,
      left: centeredLeft,
      width: maxW,
    };
  }
  return {
    top: rect.bottom + gap,
    left: centeredLeft,
    width: maxW,
  };
}

/**
 * SaaS-style coach marks over the live doctor UI.
 * Highlights real controls via data-tour attributes.
 */
export default function DoctorProductTour() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { doctor, refreshDoctor } = useDoctorSession();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [finishing, setFinishing] = useState(false);

  const active = Boolean(doctor && !doctor.onboarding_completed && !finishing);
  const step = DOCTOR_TOUR_STEPS[index] || DOCTOR_TOUR_STEPS[0];
  const isLast = index >= DOCTOR_TOUR_STEPS.length - 1;

  const finish = useCallback(async () => {
    setFinishing(true);
    try {
      await doctorUpdateMe({ onboarding_completed: true });
      await refreshDoctor();
    } catch {
      // allow exit even if patch fails
    }
    navigate(ROUTES.DOCTOR_PATIENTS, { replace: true });
  }, [navigate, refreshDoctor]);

  // Navigate to the page for the current step
  useEffect(() => {
    if (!active || !step?.route) return undefined;
    if (location.pathname !== step.route) {
      navigate(step.route, { replace: true });
    }
    return undefined;
  }, [active, step?.route, location.pathname, navigate]);

  // Measure spotlight target (retry until mounted after route change)
  useLayoutEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      setRect(measureTarget(step?.target));
    };
    tick();
    const id = window.setInterval(tick, 150);
    window.addEventListener('resize', tick);
    window.addEventListener('scroll', tick, true);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('resize', tick);
      window.removeEventListener('scroll', tick, true);
    };
  }, [active, index, step?.target, location.pathname]);

  if (!active) return null;

  const hole = rect
    ? {
        top: Math.max(0, rect.top - PAD),
        left: Math.max(0, rect.left - PAD),
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  const tip = tooltipStyle(rect, step.placement);

  return createPortal(
    <div className="fixed inset-0 z-[450]" role="dialog" aria-modal="true" aria-label={t(step.titleKey)}>
      {/* Dim layer with spotlight cutout */}
      {hole ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute rounded-2xl ring-2 ring-white/90 transition-all duration-200"
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.62)',
          }}
        />
      ) : (
        <div aria-hidden="true" className="absolute inset-0 bg-slate-900/60" />
      )}

      {/* Block interaction with the app while touring */}
      <div className="absolute inset-0" aria-hidden="true" />

      <div
        className="pointer-events-auto absolute z-[1] rounded-2xl bg-white p-4 shadow-xl"
        style={tip}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">
              {t('doctorTourStep', { n: index + 1, total: DOCTOR_TOUR_STEPS.length })}
            </p>
            <h2 className="mt-1 text-base font-bold text-slate-900">{t(step.titleKey)}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{t(step.bodyKey)}</p>
          </div>
          <button
            type="button"
            onClick={finish}
            className="shrink-0 text-sm font-medium text-gray-400 hover:text-gray-600"
          >
            {t('skip')}
          </button>
        </div>

        <div className="mb-3 flex gap-1.5">
          {DOCTOR_TOUR_STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-5 bg-primary' : 'w-1.5 bg-gray-200'
              }`}
            />
          ))}
        </div>

        <div className="flex gap-2">
          {index > 0 ? (
            <button
              type="button"
              onClick={() => setIndex((v) => Math.max(0, v - 1))}
              className="flex-1 rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-semibold text-slate-700"
            >
              {t('back')}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              if (isLast) finish();
              else setIndex((v) => v + 1);
            }}
            className="flex-[1.4] rounded-xl bg-primary px-3 py-2.5 text-sm font-semibold text-white"
          >
            {isLast ? t('doctorGetStarted') : t('next')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
